// In-memory cache for slow-changing reference data (UOMs, tax codes, item groups,
// warehouses, sales reps, item/customer lists, …). These datasets are refetched by
// almost every form on mount; on tenants with thousands of rows that repeated
// network + parse cost is a big share of "opening a document feels slow".
//
// Design:
//   - cachedRequest(key, fetcher, ttlMs) returns a cached promise while fresh, and
//     also coalesces concurrent callers onto a single in-flight request.
//   - A rejected fetch is evicted so the next call retries (never caches an error).
//   - Mutations invalidate by key prefix (invalidateReference) so a create/update
//     is visible immediately instead of waiting out the TTL.
//   - clearReferenceCache() wipes everything — MUST be called on login/logout so a
//     different tenant never sees another tenant's cached reference data.
//
// Cached values are treated as read-only. Callers already consume responses
// immutably (setState + map/filter/find); do not mutate a cached response in place.
//
// CROSS-TAB COHERENCE
//   `store` lives per tab. The everyday ERP flow is two of them: a half-captured
//   Sales Order in one, "create the item I forgot" in the other. The create busts
//   the item list in ITS tab only, so the order tab keeps serving the list it
//   cached before the item existed and the picker cannot offer it until a reload
//   throws the typed-in document away. So invalidations are broadcast to the other
//   tabs of this origin, and subscribers (subscribeReference) are told, which lets
//   an open screen pull a fresh copy in place instead of making the user reload.
//
//   Only key prefixes ever cross the channel, never cached rows. An invalidation
//   that lands on a tab that did not need it costs one refetch; shipping rows
//   between tabs would risk showing them under the wrong session.

const store = new Map(); // key -> { at: epochMs, ttl: ms, promise }

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

const CHANNEL_NAME = 'journey:reference-cache';

const subscribers = new Set(); // { prefix, handler }

// Drop every key equal to `prefix` or beginning with `${prefix}:`. A null prefix
// means "everything".
const evict = (prefix) => {
  if (!prefix) { store.clear(); return; }
  for (const key of [...store.keys()]) {
    if (key === prefix || key.startsWith(`${prefix}:`)) store.delete(key);
  }
};

// A screen watching 'inv:items' has to hear the exact bust ('inv:items'), a wider
// one ('inv', or a full clear), and a narrower one ('inv:items:{…}') — in every
// case the rows it is showing may have moved. Hence matching in both directions.
const matchesPrefix = (watched, invalidated) => (
  !invalidated
  || watched === invalidated
  || watched.startsWith(`${invalidated}:`)
  || invalidated.startsWith(`${watched}:`)
);

const notifySubscribers = (prefix) => {
  // Snapshot first: a handler is allowed to unsubscribe itself.
  [...subscribers].forEach((sub) => {
    if (!matchesPrefix(sub.prefix, prefix)) return;
    // One broken listener must not stop the others from being told.
    try { sub.handler(prefix); } catch { /* ignore */ }
  });
};

// ── The other tabs ──────────────────────────────────────────────────────────
// BroadcastChannel where available; otherwise a localStorage write, whose
// `storage` event fires in every OTHER tab of the origin and not in this one —
// which is exactly the delivery we want. Both are wrapped because a locked-down
// browser (or private mode with storage denied) must degrade to the old
// single-tab behaviour rather than break the app.
let channel = null;
try {
  if (typeof BroadcastChannel !== 'undefined') channel = new BroadcastChannel(CHANNEL_NAME);
} catch { channel = null; }

const applyRemote = (message) => {
  if (!message || typeof message !== 'object') return;
  const prefix = message.prefix || null;
  evict(prefix);
  // A 'clear' is a login/logout wipe. Evict, but do not wake the open screens:
  // the session it belonged to is gone, so every refetch it triggered would fire
  // against a dead or not-yet-issued token.
  if (message.type === 'invalidate') notifySubscribers(prefix);
};

if (channel) {
  channel.onmessage = (event) => applyRemote(event?.data);
} else if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== CHANNEL_NAME || !event.newValue) return;
    try { applyRemote(JSON.parse(event.newValue)); } catch { /* malformed — ignore */ }
  });
}

const publish = (type, prefix) => {
  const message = { type, prefix: prefix || null };
  try {
    if (channel) { channel.postMessage(message); return; }
    // `nonce` only exists to make each write a distinct value: localStorage does
    // not raise `storage` when the value is unchanged, so two identical busts in
    // a row would leave the second one undelivered.
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(CHANNEL_NAME, JSON.stringify({ ...message, nonce: `${Date.now()}:${Math.random()}` }));
    }
  } catch { /* channel closed or storage blocked — stay single-tab */ }
};

export function cachedRequest(key, fetcher, ttlMs = DEFAULT_TTL_MS) {
  const hit = store.get(key);
  if (hit && (Date.now() - hit.at) < hit.ttl) {
    return hit.promise;
  }

  const promise = Promise.resolve().then(fetcher);
  store.set(key, { at: Date.now(), ttl: ttlMs, promise });

  // Evict on failure so we retry next time instead of serving a cached rejection.
  promise.catch(() => {
    const current = store.get(key);
    if (current && current.promise === promise) store.delete(key);
  });

  return promise;
}

// Invalidate every key equal to `prefix` or beginning with `${prefix}:`.
// e.g. invalidateReference('inv:items') clears 'inv:items' and 'inv:items:{...}'.
// Also tells this tab's subscribers and the other tabs of this origin.
export function invalidateReference(prefix) {
  const normalized = prefix || null;
  evict(normalized);
  notifySubscribers(normalized);
  publish('invalidate', normalized);
}

// Drop this tab's copy WITHOUT telling anyone. For a caller that is about to
// refetch the data itself: notifying would re-enter the very subscriber that
// asked for the refresh, and the other tabs have no reason to throw theirs away.
export function expireReference(prefix) {
  evict(prefix || null);
}

// Watch a key prefix. `handler` runs whenever that data is invalidated — here or
// in another tab — so an open screen can reload the rows it is displaying.
// Returns an unsubscribe function.
export function subscribeReference(prefix, handler) {
  if (!prefix || typeof handler !== 'function') return () => {};
  const sub = { prefix, handler };
  subscribers.add(sub);
  return () => { subscribers.delete(sub); };
}

export function clearReferenceCache() {
  store.clear();
  publish('clear', null);
}
